// OCT-7340: TreatyApp: User Management — add an Action History view of admin activity - Frontend : ashish.atwal@resolutionlife.com
// The Action History results table. Renders Date & Time, User (avatar + name),
// Action (colour-coded Tag), and Details. The Date & Time, User, and Action
// column headers are clickable to sort client-side, toggling ascending/descending.
import { FC, useMemo, useState } from 'react';
import {
  Avatar,
  Flex,
  Icon,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { BiChevronDown, BiChevronUp } from 'react-icons/bi';

import { ActionHistoryEntryFragment } from '../../graphql/generated';
import { getActionColorScheme } from './action-type-styles';

type SortColumn = 'timestamp' | 'user' | 'action';
type SortDirection = 'asc' | 'desc';

type Props = {
  entries: ActionHistoryEntryFragment[];
};

const actorName = (entry: ActionHistoryEntryFragment): string =>
  entry.actor.name || entry.actor.email || 'Unknown user';

const formatDateTime = (isoTimestamp: string): string => {
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) {
    return isoTimestamp;
  }
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const ActionHistoryTable: FC<Props> = ({ entries }) => {
  // Default: most recent first.
  const [sortColumn, setSortColumn] = useState<SortColumn>('timestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (column: SortColumn) => {
    if (column === sortColumn) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      // Timestamp defaults to newest-first; text columns default to A→Z.
      setSortDirection(column === 'timestamp' ? 'desc' : 'asc');
    }
  };

  const sortedEntries = useMemo(() => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    // toSorted is not available in this target; copy before sorting to avoid
    // mutating the source array.
    return [...entries].sort((a, b) => {
      if (sortColumn === 'timestamp') {
        return (
          (new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()) *
          direction
        );
      }
      if (sortColumn === 'user') {
        return actorName(a).localeCompare(actorName(b)) * direction;
      }
      return a.actionType.localeCompare(b.actionType) * direction;
    });
  }, [entries, sortColumn, sortDirection]);

  const sortIcon = (column: SortColumn) => {
    if (column !== sortColumn) {
      return null;
    }
    return (
      <Icon
        as={sortDirection === 'asc' ? BiChevronUp : BiChevronDown}
        ml={1}
        aria-label={sortDirection === 'asc' ? 'ascending' : 'descending'}
      />
    );
  };

  return (
    <TableContainer>
      <Table variant="unstyled" size="sm">
        <Thead>
          <Tr color="gray.600" borderBottom="1px solid" borderColor="gray.200">
            <Th
              cursor="pointer"
              userSelect="none"
              onClick={() => handleSort('timestamp')}
              data-testid="action-history-sort-timestamp"
            >
              Date &amp; Time
              {sortIcon('timestamp')}
            </Th>
            <Th
              cursor="pointer"
              userSelect="none"
              onClick={() => handleSort('user')}
              data-testid="action-history-sort-user"
            >
              User
              {sortIcon('user')}
            </Th>
            <Th
              cursor="pointer"
              userSelect="none"
              onClick={() => handleSort('action')}
              data-testid="action-history-sort-action"
            >
              Action
              {sortIcon('action')}
            </Th>
            <Th>Details</Th>
          </Tr>
        </Thead>

        <Tbody>
          {sortedEntries.map((entry) => (
            <Tr
              key={entry.id}
              data-testid="action-history-row"
              borderBottom="1px solid"
              borderColor="gray.100"
            >
              <Td whiteSpace="nowrap" color="gray.600" fontSize="sm">
                {formatDateTime(entry.timestamp)}
              </Td>
              <Td>
                <Flex align="center" gridGap={2}>
                  <Avatar size="xs" name={actorName(entry)} />
                  <Text fontSize="sm" fontWeight="medium">
                    {actorName(entry)}
                  </Text>
                </Flex>
              </Td>
              <Td whiteSpace="nowrap">
                <Tag
                  colorScheme={getActionColorScheme(entry.actionType)}
                  size="sm"
                >
                  {entry.actionType}
                </Tag>
              </Td>
              <Td color="gray.700" fontSize="sm" whiteSpace="normal">
                {entry.details}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};
